import React, { useEffect, useState } from "react";
import { useSelect } from "@refinedev/antd";
import { Form, Input, Modal, Select, message } from "antd";
import { ITenant } from "interfaces";
import { ROLE_RES_ID, TENANT_RES_ID } from "utility/constants";
import useHttpClient from "components/hooks/useHttpClient";
import { getUserRoles } from "utility/helper";

type InviteUserDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Preselect organisation (current tenant). */
  defaultTenantId?: number | null;
};

type InviteFormValues = {
  email: string;
  tenantId: number;
  roles: string[];
};

const ROLE_DESCRIPTIONS: Record<string, string> = {
  Admin: "Full behörighet till alla delar av systemet",
  Standard: "Begränsad roll med behörighet att hantera produkter",
  StoreAccess: "Behörighet att logga in i butikens system",
  Supplier: "Behörighet till partnerportalen",
};

export const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  defaultTenantId,
}) => {
  const [form] = Form.useForm<InviteFormValues>();
  const [loading, setLoading] = useState(false);
  const { makeHttpRequestThrowErrors } = useHttpClient();

  const { selectProps: tenantProps } = useSelect<ITenant>({
    resource: TENANT_RES_ID,
    optionLabel: "name",
    optionValue: "id",
  });

  const { selectProps: roleProps } = useSelect({
    resource: ROLE_RES_ID,
    optionLabel: "name",
    optionValue: "name",
  });

  const canAssignSuperAdmin = getUserRoles().includes("SuperAdmin");
  const roleOptions = (roleProps.options ?? [])
    .filter((option) => canAssignSuperAdmin || option.value !== "SuperAdmin")
    .map((option) => {
      const roleName = String(option.value ?? option.label ?? "");
      return {
        ...option,
        value: roleName,
        label: roleName,
      };
    });

  useEffect(() => {
    if (!open) {
      return;
    }
    if (defaultTenantId != null && !Number.isNaN(defaultTenantId)) {
      form.setFieldsValue({ tenantId: defaultTenantId });
    }
  }, [open, defaultTenantId, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await makeHttpRequestThrowErrors("POST", "users/invite", {
        email: values.email,
        tenantId: values.tenantId,
        roles: values.roles,
      });
      message.success("Inbjudan skickad");
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error: any) {
      const apiMessage =
        error?.message ||
        error?.Message ||
        (typeof error === "string" ? error : null) ||
        "Kunde inte skicka inbjudan";
      message.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Bjud in användare"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={handleOk}
      okText="Skicka inbjudan"
      cancelText="Avbryt"
      confirmLoading={loading}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="E-postadress"
          name="email"
          rules={[{ required: true, type: "email", message: "Ange en giltig e-postadress" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Organisation"
          name="tenantId"
          rules={[{ required: true, message: "Välj organisation" }]}
        >
          <Select {...tenantProps} />
        </Form.Item>
        <Form.Item
          label="Roller"
          name="roles"
          rules={[{ required: true, message: "Välj minst en roll" }]}
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="Välj roller"
            {...roleProps}
            options={roleOptions}
            optionRender={(option) => {
              const roleName = String(option.value ?? "");
              const description = ROLE_DESCRIPTIONS[roleName];
              if (!description) {
                return roleName;
              }
              return (
                <span>
                  <strong>{roleName}</strong>
                  {" - "}
                  <span style={{ color: "rgba(0,0,0,0.45)", fontWeight: 400 }}>
                    {description}
                  </span>
                </span>
              );
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
